<template>
  <div class="user-audit-log">
    <div v-if="hasSummary" class="row mb-3">
      <div class="col-md-6">
        <p class="mb-1 text-muted small">Creado</p>
        <p class="mb-0">
          <strong>{{ formatTimestamp(createdAt) }}</strong>
          <span v-if="createdBy" class="text-muted"> · {{ formatActor(createdBy) }}</span>
        </p>
      </div>
      <div v-if="updatedAt && updatedAt !== createdAt" class="col-md-6">
        <p class="mb-1 text-muted small">Última modificación</p>
        <p class="mb-0">
          <strong>{{ formatTimestamp(updatedAt) }}</strong>
          <span v-if="updatedBy" class="text-muted"> · {{ formatActor(updatedBy) }}</span>
        </p>
      </div>
    </div>

    <div v-if="entries.length">
      <h6 class="mb-2">Historial de cambios</h6>
      <div class="table-responsive">
        <table class="table table-sm table-bordered mb-0">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Acción</th>
              <th>Usuario</th>
              <th>Detalle</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="entry in entries" :key="entry.id">
              <td class="text-nowrap">{{ formatTimestamp(entry.at) }}</td>
              <td>{{ getActionLabel(entry.action) }}</td>
              <td>{{ formatActor(entry.by) }}</td>
              <td>
                <template v-if="entry.action === 'created' && entry.snapshot">
                  Usuario registrado con la configuración inicial.
                </template>
                <template v-else-if="entry.action === 'password_reset_sent'">
                  Correo de restablecimiento enviado
                  <span v-if="entry.meta?.email"> a {{ entry.meta.email }}</span>.
                </template>
                <template v-else-if="entry.action === 'password_changed'">
                  Contraseña actualizada (sin registrar valores sensibles).
                </template>
                <template v-else-if="entry.action === 'deleted' && entry.snapshot">
                  Usuario eliminado. Estado previo registrado.
                </template>
                <template v-else-if="entry.changes">
                  <ul class="mb-0 ps-3">
                    <li v-for="(change, field) in entry.changes" :key="field">
                      <strong>{{ getFieldLabel(field) }}:</strong>
                      {{ formatFieldValue(field, change.from) }}
                      →
                      {{ formatFieldValue(field, change.to) }}
                    </li>
                  </ul>
                </template>
                <template v-else>—</template>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <p v-else class="text-muted mb-0">
      {{ emptyMessage }}
    </p>
  </div>
</template>

<script>
import {
  USER_AUDIT_FIELD_LABELS,
  USER_AUDIT_ACTION_LABELS,
  formatUserActor,
  formatDefaultStoreForAudit,
  getSortedUserAuditLogEntries,
} from '@/utils/userAudit'

export default {
  name: 'UserAuditLog',
  props: {
    auditLog: {
      type: Object,
      default: null,
    },
    createdAt: {
      type: [Number, String],
      default: null,
    },
    createdBy: {
      type: Object,
      default: null,
    },
    updatedAt: {
      type: [Number, String],
      default: null,
    },
    updatedBy: {
      type: Object,
      default: null,
    },
    stores: {
      type: Array,
      default: () => [],
    },
    emptyMessage: {
      type: String,
      default:
        'Este usuario no tiene historial de auditoría (registro anterior a la trazabilidad).',
    },
  },
  computed: {
    entries() {
      return getSortedUserAuditLogEntries(this.auditLog)
    },
    hasSummary() {
      return Boolean(this.createdAt || this.createdBy || this.updatedAt || this.updatedBy)
    },
  },
  methods: {
    formatActor: formatUserActor,
    getActionLabel(action) {
      return USER_AUDIT_ACTION_LABELS[action] || action || 'N/A'
    },
    getFieldLabel(field) {
      return USER_AUDIT_FIELD_LABELS[field] || field
    },
    formatTimestamp(timestamp) {
      if (!timestamp) return 'N/A'
      try {
        const date = new Date(Number(timestamp))
        const day = String(date.getDate()).padStart(2, '0')
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const year = date.getFullYear()
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        return `${day}/${month}/${year} ${hours}:${minutes}`
      } catch {
        return 'N/A'
      }
    },
    formatFieldValue(field, value) {
      if (field === 'defaultStore') {
        return formatDefaultStoreForAudit(value, this.stores)
      }

      if (value === undefined || value === null || value === '') return '—'

      if (field === 'status') {
        const status = String(value).toLowerCase()
        if (status === 'activo') return 'Activo'
        if (status === 'inactivo') return 'Inactivo'
        return String(value)
      }

      return String(value)
    },
  },
}
</script>
